/**
 * DECIR EN VOZ ALTA LO QUE PASA EN EL LIENZO.
 *
 * Un lienzo es una caja negra para quien no ve: es un `<canvas>`, y dentro no
 * hay nada que un lector de pantalla pueda leer. Quien navega con teclado puede
 * mover una capa con las flechas y no enterarse de si se ha movido.
 *
 * Las medidas en milimetros YA estan calculadas —son las mismas que se validan
 * y las mismas que llegan al taller—, asi que decirlas en voz alta es barato.
 * Lo que hay que hacer bien es DECIR LO JUSTO: un anuncio en cada pixel de un
 * arrastre convierte el lector de pantalla en ruido y la gente lo apaga.
 */
import { layerBBoxMm, type Layer } from '@besbello/design-core';

/** Un numero legible en voz alta: sin decimales que nadie necesita oir. */
function mm(valor: number): string {
  return `${Math.round(valor)} mm`;
}

function tipoDeCapa(capa: Layer): string {
  if (capa.kind === 'text') return `texto «${capa.text.replace(/\n/g, ' ').slice(0, 40)}»`;
  if (capa.kind === 'image') return 'imagen';
  return 'forma';
}

/**
 * Como se describe una capa: que es, donde esta y cuanto mide.
 *
 * Ese es el orden a proposito: quien escucha necesita saber PRIMERO de que se
 * le habla. Empezar por las coordenadas obliga a esperar al final para saber si
 * le interesa.
 */
export function describirCapa(capa: Layer): string {
  const b = layerBBoxMm(capa);
  const ancho = b.maxX - b.minX;
  const alto = b.maxY - b.minY;
  const bloqueada = capa.locked ? ', bloqueada' : '';
  return `${tipoDeCapa(capa)}, en ${mm(b.minX)} por ${mm(b.minY)}, de ${mm(ancho)} por ${mm(alto)}${bloqueada}`;
}

/** Al seleccionar: se dice cual de cuantas, para poder orientarse en la lista. */
export function anunciarSeleccion(capa: Layer, indice: number, total: number): string {
  return `Seleccionada capa ${indice + 1} de ${total}: ${describirCapa(capa)}`;
}

/**
 * Al mover con las flechas: solo la POSICION.
 *
 * Repetir el tipo y el tamano en cada pulsacion es exactamente lo que hace que
 * un lector de pantalla resulte insoportable. Quien acaba de seleccionar la
 * capa ya sabe cual es.
 */
export function anunciarMovimiento(capa: Layer): string {
  const b = layerBBoxMm(capa);
  return `${mm(b.minX)} por ${mm(b.minY)}`;
}

/** Al redimensionar: solo la MEDIDA, por el mismo motivo. */
export function anunciarTamano(capa: Layer): string {
  const b = layerBBoxMm(capa);
  return `${mm(b.maxX - b.minX)} por ${mm(b.maxY - b.minY)}`;
}

/**
 * Cuando una capa se sale de la zona.
 *
 * Es el unico aviso que se interrumpe al usuario en mitad de un gesto, porque
 * es el unico que cambia lo que va a llegar impreso.
 */
export function anunciarFueraDeZona(capa: Layer, zona: { widthMm: number; heightMm: number }): string | null {
  const b = layerBBoxMm(capa);
  const fuera =
    b.minX < -0.01 || b.minY < -0.01 || b.maxX > zona.widthMm + 0.01 || b.maxY > zona.heightMm + 0.01;
  if (!fuera) return null;

  const cuanto = Math.max(
    -b.minX,
    -b.minY,
    b.maxX - zona.widthMm,
    b.maxY - zona.heightMm,
  );
  return `Atención: se sale ${mm(cuanto)} de la zona imprimible`;
}

/** Cuantas capas hay y cuantas dan error, para el resumen del documento. */
export function describirDocumento(capas: readonly Layer[], errores: number): string {
  if (capas.length === 0) return 'Diseño vacío';
  const cuenta = `${capas.length} ${capas.length === 1 ? 'elemento' : 'elementos'}`;
  if (errores === 0) return `${cuenta}, sin avisos`;
  return `${cuenta}, ${errores} ${errores === 1 ? 'aviso' : 'avisos'} que impiden imprimir`;
}
